import type {ButtonProps} from "@mui/material/Button";
import {GoldButton, PrimaryButton, SecondaryButton} from "./BaseButton.styles";

/**
 * The repo's shared button, beside {@link ./BaseDialog}, {@link ./BaseInput} and {@link ./BaseSelect}
 * and following their contract: props in, `theme.tokens` out, no logic, no colour literals.
 *
 * **Why it exists** (owner, 2026-08-29: "don't we use standard mui buttons? … we need to establish
 * some base button"). This app has had MUI v9 everywhere and no shared button, so every surface styled
 * its own — which is how one card's two actions came to render 48px against 52px (ui#1674) while the
 * recipient page's download CTA stood at 54px. Nothing was wrong with any individual rule; there was
 * simply no place for the answer to "how tall is a button here" to live. That place is
 * `theme/tokens.ts`'s `ACTION_CONTROL_HEIGHT_PX`, a design token beside `radii.control`; equal height
 * across variants is then structural rather than arithmetic — see the border note in
 * `BaseButton.styles.ts`.
 *
 * **ADOPTION IS DELIBERATELY NARROW, and this note is the policy rather than an apology.** The only
 * surface converted when this landed is the recipient page's Download-all button, the one the owner
 * reported drifting — because that page is what the referral-chip ticket was already reworking. The
 * 20-odd other hand-styled buttons are NOT migrated here: a mass migration is exactly the
 * "replace-don't-accumulate" rule inverted, spending a large untested diff on surfaces nobody is
 * looking at, and this repo has no UI-component tests to catch what it breaks. The primitive spreads
 * the way the externalized-styles rule spreads — a surface adopts it when that surface is next
 * substantially reworked. If you are here because you are reworking one, convert it.
 *
 * The variant names are the ROLE the button plays, never its colour-as-decoration: `primary` is the
 * one action a surface exists for, `secondary` the quieter half of a pair, `gold` the upgrade/PRO
 * accent. A surface with two `primary` buttons has a content problem, not a styling one.
 */
export type BaseButtonVariant = "primary" | "secondary" | "gold";

/**
 * How tall the button stands: `action` is the design system's own control height, `compact` one size
 * down (kitecorp/transferify-ui#1922, owner: "download all button height should be reduced").
 *
 * A SIZE rather than a height, for the reason this primitive exists: the answer to "how tall is a
 * button here" belongs to the token layer, and a surface that needs a smaller one picks from the sizes
 * the system offers instead of declaring a number of its own. Both values live in `theme/tokens.ts`.
 *
 * `compact` shrinks what is PAINTED and not what can be pressed — see the tap-target overlay in
 * `BaseButton.styles.ts`, which extends the button's own hit area back to the 44px platform minimum.
 */
export type BaseButtonSize = "action" | "compact";

/**
 * Everything MUI's `Button` accepts — `onClick`, `disabled`, `type`, `href`, `startIcon`, `fullWidth`,
 * `aria-*`, children — forwarded verbatim.
 *
 * `variant` is REPLACED rather than extended: MUI's own values (`text`/`outlined`/`contained`) name a
 * fill treatment, and this primitive names a role, so accepting both spellings on one prop would make
 * `variant="outlined"` mean two different things depending on which layer read it. `color` goes with
 * it, because every colour here comes from the variant and the token layer; a call site that needs a
 * colour MUI's palette does not have would otherwise reach for it and re-open the drift.
 */
export type BaseButtonProps = Omit<ButtonProps, "variant" | "color" | "size"> & {
	variant?: BaseButtonVariant;
	/**
	 * `size` is REPLACED for the same reason `variant` is: MUI's own `small`/`medium`/`large` set a
	 * font scale and a padding of their own, which is exactly the per-surface height drift this
	 * primitive was created to end. See {@link BaseButtonSize}.
	 */
	size?: BaseButtonSize;
};

/**
 * Role name → the styled root that paints it.
 *
 * A lookup rather than a chain of ternaries in the render, and a lookup rather than one root reading
 * a `$variant` transient prop: three separate styled components each declare a complete, readable
 * rule, and styled-components generates one class per variant instead of recomputing interpolations
 * per instance.
 */
const VARIANT_ROOTS = {
	primary: PrimaryButton,
	secondary: SecondaryButton,
	gold: GoldButton,
} as const satisfies Record<BaseButtonVariant, unknown>;

export default function BaseButton({variant = "primary", size = "action", ...others}: BaseButtonProps) {
	const Root = VARIANT_ROOTS[variant];
	// MUI's `text` variant underneath every role: it is the one that brings no fill, no elevation and
	// no border of its own for `BaseButton.styles.ts` to override. The roles' fills are painted there.
	//
	// The size travels as a DATA ATTRIBUTE rather than as a styled prop: the three roles are three
	// separate styled components, so a prop would be threaded through all three and mint a class per
	// role per size, where one attribute rule in the shared box model covers every role at once. The
	// default size sets no attribute at all, so a button that never asked for a size renders exactly
	// the markup it did before the option existed.
	return <Root variant="text" data-size={size === "action" ? undefined : size} {...others}/>;
}
